import type { ApiClient } from '@carbonwebmail/api-client'
import type { TemplateDto, TemplateListDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * The saved templates, and what can be done to one that already exists.
 *
 * **Nothing is written here.** Making and changing a template is Compose, which
 * is a screen of its own because laying out an email needs the width. This
 * screen lists, filters, copies and deletes; the two buttons that look like
 * writing are links to Compose.
 *
 * The filter is applied to the rows here rather than sent to the server,
 * unlike the contacts list's. This listing is not paged — templates are counted
 * in dozens, not thousands — so filtering what is already loaded answers the
 * same question the server would, without the round trip.
 */

/** The two kinds a body can be, and everything. There is no third. */
export type Kind = 'all' | 'designed' | 'prose'

export type TemplatesState = AsyncState<TemplateListDto>

export function useTemplates({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.templates(), [client])
  const { state, reload } = useLoaded<TemplateListDto>(
    load,
    'We could not load your templates. Please try again in a moment.',
  )

  const [kind, setKind] = useState<Kind>('all')
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  /** One flag, because both actions take the whole gallery out of use. */
  const [busy, setBusy] = useState(false)

  /**
   * A copy, under a name the server picks.
   *
   * Starting from a copy is how most templates after the first get written. The
   * name comes back rather than being guessed here, because the browser cannot
   * see the other names — a client that guessed would offer a name it then had
   * to refuse.
   */
  const duplicate = useCallback(
    async (template: TemplateDto) => {
      setBusy(true)
      setProblem(undefined)
      setNotice(undefined)
      try {
        const copied = await client.duplicateTemplate(template.templateId)
        if (copied === 'not-found') {
          setProblem('That template is no longer there. Somebody may have deleted it.')
          await reload()
          return
        }
        setNotice(`Copied as "${copied.name}".`)
        await reload()
      } catch {
        setProblem('We could not copy that template. Please try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client, reload],
  )

  const remove = useCallback(
    async (template: TemplateDto) => {
      /*
       * Asked, because there is no undo and the button sits beside Duplicate.
       * Nothing points at a template — a campaign took a copy — so deleting one
       * erases no record of anything, which is exactly why it is easy to do by
       * accident and worth one question.
       */
      if (!window.confirm(`Delete "${template.name}"? This cannot be undone.`)) return

      setBusy(true)
      setProblem(undefined)
      setNotice(undefined)
      try {
        await client.deleteTemplate(template.templateId)
        setNotice(`Deleted "${template.name}".`)
        await reload()
      } catch {
        setProblem('We could not delete that template. Please try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client, reload],
  )

  return { state, kind, setKind, problem, notice, busy, duplicate, remove, retry: reload }
}
